import { z } from '@/utils/zod'

//
// App Failure Codes
//

const appFailureCodes = [
    'invalidRequest',
    'authenticationRequired',
    'authenticationUnavailable',
    'permissionDenied',
    'resourceNotFound',
    'stateNotFound',
    'stateConsumed',
    'invalidState',
    'providerDisabled',
    'signupUnavailable',
    'providerError',
    'featureDisabled',
    'factorUnsupported',
    'internalError',
] as const

export const AppFailureCodeSchema = z.enum(appFailureCodes)

export type AppFailureCode = z.infer<typeof AppFailureCodeSchema>

//
// OAuth Protocol Failure Codes
//

const oauthProtocolFailureCodeMap = {
    invalid_request: 'invalidRequest',
    unauthorized_client: 'unauthorizedClient',
    access_denied: 'accessDenied',
    unsupported_response_type: 'unsupportedResponseType',
    invalid_scope: 'invalidScope',
    server_error: 'serverError',
    temporarily_unavailable: 'temporarilyUnavailable',
    interaction_required: 'interactionRequired',
    login_required: 'loginRequired',
    account_selection_required: 'accountSelectionRequired',
    consent_required: 'consentRequired',
} as const

type OauthProtocolErrorCode = keyof typeof oauthProtocolFailureCodeMap

const oauthMappedProtocolFailureCodes = Object.values(oauthProtocolFailureCodeMap)

const isOauthProtocolErrorCode = (errorCode: string): errorCode is OauthProtocolErrorCode => {
    return Object.hasOwn(oauthProtocolFailureCodeMap, errorCode)
}

//
// OIDC Provider Protocol Failure Codes
//

const oidcProviderProtocolFailureCodeMap = {
    invalid_token: 'invalidToken',
    invalid_redirect_uri: 'invalidRedirectUri',
    invalid_client_metadata: 'invalidClientMetadata',
    invalid_scope: 'invalidScope',
    insufficient_scope: 'insufficientScope',
    invalid_request: 'invalidRequest',
    invalid_client: 'invalidClient',
    invalid_grant: 'invalidGrant',
    web_message_uri_mismatch: 'webMessageUriMismatch',
    access_denied: 'accessDenied',
    authorization_pending: 'authorizationPending',
    consent_required: 'consentRequired',
    expired_login_hint_token: 'expiredLoginHintToken',
    expired_token: 'expiredToken',
    interaction_required: 'interactionRequired',
    invalid_binding_message: 'invalidBindingMessage',
    invalid_dpop_proof: 'invalidDpopProof',
    invalid_request_object: 'invalidRequestObject',
    invalid_request_uri: 'invalidRequestUri',
    invalid_software_statement: 'invalidSoftwareStatement',
    invalid_target: 'invalidTarget',
    invalid_user_code: 'invalidUserCode',
    login_required: 'loginRequired',
    missing_user_code: 'missingUserCode',
    registration_not_supported: 'registrationNotSupported',
    request_not_supported: 'requestNotSupported',
    request_uri_not_supported: 'requestUriNotSupported',
    slow_down: 'slowDown',
    temporarily_unavailable: 'temporarilyUnavailable',
    transaction_failed: 'transactionFailed',
    unapproved_software_statement: 'unapprovedSoftwareStatement',
    unauthorized_client: 'unauthorizedClient',
    unknown_user_id: 'unknownUserId',
    unsupported_grant_type: 'unsupportedGrantType',
    unsupported_response_mode: 'unsupportedResponseMode',
    unsupported_response_type: 'unsupportedResponseType',
} as const

type OidcProviderProtocolErrorCode = keyof typeof oidcProviderProtocolFailureCodeMap

const oidcProviderMappedProtocolFailureCodes = Object.values(oidcProviderProtocolFailureCodeMap)

const isOidcProviderProtocolErrorCode = (errorCode: string): errorCode is OidcProviderProtocolErrorCode => {
    return Object.hasOwn(oidcProviderProtocolFailureCodeMap, errorCode)
}

//
// Protocol Failure Codes
//

export const ProtocolFailureCodeSchema = z.enum([
    ...oauthMappedProtocolFailureCodes,
    ...oidcProviderMappedProtocolFailureCodes,
])

export type ProtocolFailureCode = z.infer<typeof ProtocolFailureCodeSchema>

//
// Failures
//

const AppFailureSchema = z.object({
    kind: z.literal('app'),
    code: AppFailureCodeSchema,
})

type AppFailure = z.infer<typeof AppFailureSchema>

const ProtocolFailureSchema = z.object({
    kind: z.literal('protocol'),
    code: ProtocolFailureCodeSchema,
})

type ProtocolFailure = z.infer<typeof ProtocolFailureSchema>

export const FailureSchema = z.discriminatedUnion('kind', [AppFailureSchema, ProtocolFailureSchema])
export type Failure = z.infer<typeof FailureSchema>

export const appFailure = (code: AppFailureCode): AppFailure => ({
    kind: 'app',
    code,
})

const protocolFailure = (code: ProtocolFailureCode): ProtocolFailure => ({
    kind: 'protocol',
    code,
})

//
// Protocol Failure Code Mapping
//

export const mapOauthFailureOrDefault = (
    errorCode: string,
    fallback: Failure = appFailure('providerError')
): Failure => {
    if (!isOauthProtocolErrorCode(errorCode)) {
        return fallback
    }

    return protocolFailure(oauthProtocolFailureCodeMap[errorCode])
}

export const mapOidcProviderFailureOrDefault = (
    errorCode: string,
    fallback: Failure = appFailure('internalError')
): Failure => {
    if (!isOidcProviderProtocolErrorCode(errorCode)) {
        return fallback
    }

    return protocolFailure(oidcProviderProtocolFailureCodeMap[errorCode])
}
